import { company } from './siteData'
import { getContactFormProductOptions } from './productsCatalogData'

export const contactFormProductOptions = getContactFormProductOptions()

export const contactHero = {
  eyebrow: 'Contact Us',
  titleLine1: "Let's Build Strong",
  titleAccent: 'Business ',
  titleLine2: 'Relationships',
  description:
    'Connect with Torbit Metals Private Limited for metal sourcing, procurement, industrial materials, and global trading solutions.',
  primaryCta: { label: 'Request Quote', to: '#quote-form' },
  secondaryCta: { label: 'Send Inquiry', to: '#inquiry-form' },
}

export const contactInfoSection = {
  label: 'Get In Touch',
  titleLine1: 'Connect with',
  titleAccent: 'Torbit Metals',
  titleLine2: 'Private Limited',
  description:
    'Use the contact details below for product enquiries, business communication and supply coordination.',
}

export const quoteFormSection = {
  label: 'Get A Quote',
  titleLine1: 'Request Your',
  titleAccent: 'Product Quote',
  description:
    'Share product details, quantity, grade, packing and delivery location to receive a competitive quote from our trading team.',
  submitLabel: 'Submit Quote Request',
  highlights: [
    { id: 'pricing', title: 'Competitive Pricing', icon: 'pricing' },
    { id: 'delivery', title: 'Timely Delivery', icon: 'delivery' },
    { id: 'quality', title: 'Quality Assurance', icon: 'quality' },
  ],
  fields: [
    { name: 'name', label: 'Your Name', type: 'text', placeholder: 'Enter your full name', half: true },
    { name: 'phone', label: 'Phone Number', type: 'tel', placeholder: 'Enter your phone number', half: true },
    { name: 'email', label: 'Email Address', type: 'email', placeholder: 'Enter your email address', half: true },
    {
      name: 'product',
      label: 'Product Required',
      type: 'select',
      placeholder: 'Select one or more products',
      half: true,
    },
    {
      name: 'quantity',
      label: 'Quantity Required',
      type: 'text',
      placeholder: 'e.g. 25 MT, 1000 kg',
      half: true,
    },
    {
      name: 'grade',
      label: 'Grade / Specification',
      type: 'text',
      placeholder: 'e.g. ADC12, 99.7% purity',
      half: true,
    },
    {
      name: 'packing',
      label: 'Packing Preference',
      type: 'text',
      placeholder: 'e.g. Bundles, coils, loose',
      half: true,
    },
    {
      name: 'deliveryLocation',
      label: 'Delivery Location',
      type: 'text',
      placeholder: 'City, state or port of delivery',
      half: true,
    },
    {
      name: 'message',
      label: 'Additional Requirements',
      type: 'textarea',
      placeholder: 'Share timeline, payment terms or any other quote details',
      half: false,
      required: false,
    },
  ],
}

export const inquiryFormSection = {
  label: 'General Inquiry',
  titleLine1: 'Send Us Your',
  titleAccent: 'Business Inquiry',
  description:
    'For general questions, partnership discussions, documentation support or other non-quote requests, use the inquiry form below.',
  submitLabel: 'Submit Inquiry',
  highlights: [
    { id: 'support', title: 'Expert Support', icon: 'support' },
    { id: 'partnerships', title: 'Strong Business Partnerships', icon: 'partnerships' },
    { id: 'response', title: 'Quick Response', icon: 'response' },
  ],
  fields: [
    { name: 'name', label: 'Your Name', type: 'text', placeholder: 'Enter your full name', half: true },
    { name: 'phone', label: 'Phone Number', type: 'tel', placeholder: 'Enter your phone number', half: true },
    { name: 'email', label: 'Email Address', type: 'email', placeholder: 'Enter your email address', half: true },
    {
      name: 'subject',
      label: 'Inquiry Subject',
      type: 'text',
      placeholder: 'e.g. Partnership, documentation, support',
      half: true,
    },
    {
      name: 'message',
      label: 'Your Message',
      type: 'textarea',
      placeholder: 'Describe your inquiry and how we can assist you',
      half: false,
    },
  ],
}

export const contactLocationsSection = {
  label: 'Factory Network',
  titleLine1: 'Gujarat-Based',
  titleAccent: 'Operations',
  titleLine2: 'With Global Reach',
  description:
    'Torbit Metals operates from Gujarat with structured factory and branch facilities, supporting domestic supply and international trade across key markets.',
  locations: [
    {
      id: 'factory',
      title: 'Factory Address',
      address: company.factoryAddress,
    },
    {
      id: 'branch',
      title: 'Branch Office',
      address: company.branchAddress,
    },
  ],
  marketsLabel: 'Core Markets',
  markets: company.markets,
}

export const contactWhySection = {
  titleLine1: 'Why',
  titleAccent: 'Contact Us',
  items: [
    {
      id: 'fast-response',
      title: 'Fast Response',
      description: 'We value your time and ensure quick and reliable communication.',
      icon: 'clock',
      variant: 'red',
    },
    {
      id: 'global-sourcing',
      title: 'Global Sourcing Support',
      description: 'Access to a wide network of trusted suppliers across global markets.',
      icon: 'globe',
      variant: 'gold',
    },
    {
      id: 'reliable-supply',
      title: 'Reliable Material Supply',
      description: 'Quality products with consistent supply and on-time delivery.',
      icon: 'shield',
      variant: 'red',
    },
    {
      id: 'trade-assistance',
      title: 'Professional Trade Assistance',
      description: 'End-to-end support for trading, documentation, and logistics.',
      icon: 'headset',
      variant: 'gold',
    },
  ],
}

export const contactMapSection = {
  titleLine1: 'Our',
  titleAccent: 'Location',
  mapTitle: company.name,
  mapAddress: company.factoryAddress,
  mapEmbedUrl: `https://maps.google.com/maps?q=${encodeURIComponent(company.factoryAddress)}&hl=en&z=15&output=embed`,
  mapLink: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(company.factoryAddress)}`,
}

export const contactBusinessHours = {
  title: 'Business Hours',
  schedule: [
    { label: 'Monday - Friday', value: '09:00 AM - 06:00 PM' },
    { label: 'Saturday', value: '09:00 AM - 04:00 PM' },
    { label: 'Sunday', value: 'Closed', closed: true },
  ],
}

export const contactPageCta = {
  title: 'Looking for a Reliable Metal Trading Partner?',
  description:
    'Partner with Torbit Metals Private Limited for trusted sourcing, professional execution, competitive pricing, and global trade expertise.',
  primaryCta: { label: 'Request Quote', to: '#quote-form' },
  secondaryCta: { label: 'Send Inquiry', to: '#inquiry-form' },
}
